import React, { memo, useEffect, useRef, useState } from 'react';
import Modal from "react-modal";
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from "../../components/select";
import { useTranslation } from "react-i18next";

import styles from "../summary/summary.module.scss";
import useForm from '../../hooks/summary/useForm';
import validate from "../../validation/summaryValidationRules";
import SearchBookLists from './searchBook';
import LanguageSelector from '../../components/languageSelector';
import { useLanguage } from '../../context/languageContext';
import SummaryEditor from '../../components/textEditor/editor';
import Button from '../../components/button';
import PreviewImage from '../../components/image/previewImage';
import { convertToCloudFrontUrl, normalizeCoverImage, isBlankHtml } from '../../helper';
import RenderProvenance from './renderProvenance.js';
import ChapterNarration from './chapterNarration.js';
import GenerateOverviewButton from "./generateOverviewButton";
import FeedbackTally from "./feedbackTally";
import TextStats, { StatLabel } from "./textStats";
import { SHOW_NOTABLE_QUOTES } from "./hiddenSections";
import TranslatePanel from "./translatePanel";
import { useConfirm } from "../../components/confirmDialog";
import { includesFolded } from "../../helper/searchFold";

/**
 * Where to play an audio object from.
 *
 * Generated narration sits in a private bucket and carries no stored URL — the
 * API attaches a short-lived signed `playbackUrl` instead. Hand-uploaded files
 * keep their public URL and go through CloudFront. Gating the player on
 * `uploadedImageUrl` alone hid generated audio completely.
 */
// The languages the narrator can speak. NOT the form's language list: the TTS
// model has no Hindi, so hi is absent as a limit of the renderer rather than a
// choice. The server refuses anything outside this set, so the two cannot
// drift apart silently.
const NARRATION_LANGUAGES = ["en", "de", "fr", "es", "ja"];

const AUDIO_POLL_MS = 5000;

const audioSrc = (audio) => {
  if (audio?.playbackUrl) return audio.playbackUrl;
  if (audio?.uploadedImageUrl) return convertToCloudFrontUrl(audio.uploadedImageUrl);
  return null;
};

/**
 * The edit form hides "why this book matters" and "key ideas", so their
 * required-checks have to be dropped — a summary written since the add form
 * stopped collecting them has both empty and could never be saved.
 *
 * `message` goes too when nothing else remains: validate stamps it whenever
 * anything failed, and useForm gates submit on the number of keys, not on any
 * of them being displayable. Leaving it behind blocks the form with nothing on
 * screen to explain why.
 */
const validateEditSummary = (values, language) => {
  const errors = validate(values, language) ?? {};
  delete errors.whyItMatters;
  delete errors.ideas;
  // Notable Quotes is hidden (see hiddenSections.js); a required field with no
  // input would make every summary impossible to update.
  if (!SHOW_NOTABLE_QUOTES) delete errors.curatedQuotes;

  const remaining = Object.keys(errors).filter((key) => key !== "message");
  if (remaining.length === 0) delete errors.message;

  return errors;
};

const EditBookSummaryPopup = ({ summaryId, isModalVisible, closeModal, onSuccess, isPageView = false, overviewDraft = null }) => {
  const confirm = useConfirm();
  // The AI testing draft is laid over the saved summary on the FIRST load
  // only; a later reload of the summary must show what is saved.
  const draftAppliedRef = useRef(false);
  const { t } = useTranslation();
  const { languages, selectedLang, setSelectedLang, adminInfo, initialLangRef, fallbackLang } = useLanguage();

  useEffect(() => {
    if (summaryId) {
      setSelectedLang(fallbackLang);
      initialLangRef.current = false;
      handleGetParticularSummaryDetail();
    }
  }, [summaryId]);

  // Which of the two save buttons was pressed. "Update" writes English and
  // stops; "Update & translate" also carries the CHAPTERS into the other
  // languages. Never the Overview — that is the Translate control's job.
  //
  // A ref rather than state: it is read once, during the submit it belongs
  // to, and a re-render in between must not lose it.
  const translateRef = useRef(false);

  const handleUpdateSummary = async (setIsLoading, setIsSubmit, values) => {
    const translate = translateRef.current;
    translateRef.current = false;
    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.put(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/update/${summaryId}`,
        { values, translate },
        { headers }
      );

      if (response.status === true) {
        onSuccess();
        closeModal();
        // The backend now distinguishes a real write from a no-op, so an
        // unchanged save says so instead of claiming to have saved.
        const nothingChanged = /no changes/i.test(response?.message || "");
        if (nothingChanged) {
          toast.info(response.message);
        } else {
          toast.success(
            translate
              ? "Summary updated. The chapters are being translated into the other languages."
              : "Summary has been updated."
          );
        }
        setIsSubmit(false);
      } else {
        toast.error(response?.message);
        setIsSubmit(false);
      }
    } catch (error) {
      console.error("handleUpdateSummary catch error::", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      toast.error(errorMessage);
      setIsSubmit(false);
    } finally {
      setIsSubmit(false);
    }
  };

  const [particularData, setParticularData] = useState([]);

  const normalizeChapterContent = (content) => {
    if (typeof content !== "string") return content || "";
    const hasHtmlTags = /<[^>]+>/.test(content);
    if (hasHtmlTags) return content;
    return content.replace(/\\n/g, "\n");
  };

  // Job state for this summary. Also the moment the server reconciles running
  // jobs with the GPU provider, so polling here is what drives progress —
  // there is no background worker to keep alive.
  const fetchAudioJobs = React.useCallback(async () => {
    if (!summaryId) return false;
    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/audio/jobs/${summaryId}?langCode=${selectedLang}`,
        { headers }
      );
      const byChapter = {};
      for (const job of response?.data?.jobs ?? []) {
        if (job.chapterId) byChapter[job.chapterId] = job;
      }
      setAudioJobs(byChapter);
      // Reload the summary once rendering stops, so the freshly attached audio
      // (and its signed playback URL) appears without a manual refresh.
      return Boolean(response?.data?.active);
    } catch (error) {
      console.error("fetchAudioJobs error::", error);
      return false;
    }
  }, [summaryId, selectedLang, adminInfo]);

  useEffect(() => {
    let timer = null;
    let cancelled = false;

    const tick = async () => {
      const active = await fetchAudioJobs();
      if (cancelled) return;
      // Poll ONLY while something is rendering. A summary sitting open with
      // finished audio should not generate traffic for the rest of the day.
      if (active) timer = setTimeout(tick, AUDIO_POLL_MS);
    };
    tick();

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [fetchAudioJobs]);

  /**
   * Narrate one chapter — in the language on screen, or in all of them.
   *
   * `allLanguages` is the same shape the translate button has: one click,
   * every language, rather than visiting five tabs and remembering which are
   * done. The languages are the renderer's, not the form's — Hindi is missing
   * because the model cannot speak it, and the server says so rather than
   * quietly narrating four.
   */
  // Which narrator the Generate buttons use. Loaded from the backend rather
  // than hardcoded, so a deployment without Cartesia keys simply does not
  // offer it instead of offering a choice that queues work nothing can take.
  const [audioProviders, setAudioProviders] = useState([]);
  const [audioProvider, setAudioProvider] = useState("");

  useEffect(() => {
    if (!adminInfo?.token) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { data: response } = await axios.get(
          `${process.env.REACT_APP_API_ROOT}/api/admin/audio/providers`,
          { headers: { Authorization: `Bearer ${adminInfo.token}` } }
        );
        if (cancelled || !response?.status) return;
        setAudioProviders(response.data?.providers ?? []);
        setAudioProvider(response.data?.defaultProvider ?? "");
      } catch (_) {
        // Silent: narration still works on the server's default. A toast here
        // would fire on every summary opened on an older backend.
      }
    })();

    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  const handleGenerateNarration = async (chapterId, { allLanguages = false, force = false } = {}) => {
    setNarrationBusy(true);
    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.post(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/audio/generate`,
        {
          summaryId,
          chapterIds: [chapterId],
          ...(allLanguages
            ? { langCodes: NARRATION_LANGUAGES }
            : { langCode: selectedLang }),
          ...(audioProvider ? { provider: audioProvider } : {}),
          ...(force ? { force: true } : {}),
        },
        { headers }
      );
      toast.success(response.message);
      // Languages this summary does not have yet are reported rather than
      // failing the click — worth saying once, not swallowing.
      const skipped = response?.data?.rejected ?? [];
      if (skipped.length > 0) {
        toast.info(skipped.map((r) => r.error).join(" "));
      }
      fetchAudioJobs();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not start narration.");
    } finally {
      setNarrationBusy(false);
    }
  };

  const normalizeChapters = (chapters = []) =>
    (Array.isArray(chapters) ? chapters : []).map((chapter) => ({
      ...chapter,
      content: normalizeChapterContent(chapter?.content),
    }));

  // Narration jobs, keyed by chapterId. Polled only while something is
  // actually rendering — an idle panel makes no requests.
  const [audioJobs, setAudioJobs] = useState({});
  const [narrationBusy, setNarrationBusy] = useState(false);

  const {
    inputRef,
    generatingRef,

    values,
    setValues,
    isSubmit,
    errors,
    searchBookName,
    setSearchBookName,
    booksData,
    editMediaError,
    fileInputRef,
    recommendeBooks,
    relatedBooksCount,
    isConfirmModalVisible,
    setIsConfirmModalVisible,
    bookSummaryId,
    isSubmitSummary,

    handleAutoGenerateChapters,
    handleGenerateOverview,
    isGeneratingOverview,
    handleSelectLanguage,
    handleGetTranslationByLang,
    handleCreateAnIndexInChapters,
    handleRemoveAnIndexInChapters,
    handleUploadAudioFileInChapter,
    handleUploadAudioAndVideo,
    handleSearchTextChange,
    handleTextEditorChange,
    handleEditChapterValues,
    handleGetParticularBookDetail,
    handleAddValues,
    handleUpdateTranslationField,
    handleUploadMedia,
    handleRemoveCoverImage,
    handleSubmit,
  } = useForm(handleUpdateSummary, validateEditSummary, adminInfo);

  // The book's own cover, sent alongside the summary's by `getParticularSummary`.
  // Preview only — never written into `values`, so it cannot be saved as this
  // summary's cover by someone who merely opened the form and pressed Update.
  const borrowedCoverUrl = normalizeCoverImage(
    particularData?.bookCoverImage
  ).uploadedImageUrl;

  const translationValues = handleGetTranslationByLang(selectedLang) || {
    bookName: "",
    relatedBooks: [],
    authorName: [],
    genre: [],
    recommendedBy: [],
    media: { audio: null, video: null },
    aboutTheBook: "",
    tagline: "",
    whyItMatters: "",
    ideas: "",
    actionableInsights: "",
    shouldReadFor: "",
    curatedQuotes: "",
    reflectionQuestions: "",
    chapters: [],
  };

  const handleGetParticularSummaryDetail = async () => {
    initialLangRef.current = false;

    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      // axios.get takes (url, config) — a THIRD argument is silently dropped.
      // Passing `{ values }` second meant `{ headers }` never left the browser,
      // so this request carried no Authorization token. It only worked while
      // this route was unauthenticated; the moment it required auth it 401'd
      // and the catch below swallowed it, leaving a blank Edit Summary form.
      const { data: response } = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/get/particular/${summaryId}`,
        { headers }
      );

      if (response.status === true) {
        setParticularData(response.data);
        setSearchBookName('');
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (particularData && particularData.translations && !initialLangRef.current) {
      const mergedTranslations = languages.map((langCode) => {
        const existingTranslation = particularData.translations.find((item) => item.langCode === langCode);

        return (
          existingTranslation
            ? {
              ...existingTranslation,
              chapters: normalizeChapters(existingTranslation.chapters),
            }
            : {
            langCode: langCode,
            bookName: "",
            authorName: [],
            relatedBooks: [],
            recommendedBy: [],
            genre: null,
            aboutTheBook: "",
            tagline: "",
            whyItMatters: "",
            ideas: "",
            actionableInsights: "",
            shouldReadFor: "",
            curatedQuotes: "",
            reflectionQuestions: "",
            chapters: [{ content: "", audio: null }],
            media: { audio: null, video: null },
          }
        );
      });

      setValues((prevValues) => ({
        ...prevValues,
        bookSummaryId: particularData.bookSummaryBy,
        coverImage: particularData.coverImage
          ? normalizeCoverImage(particularData.coverImage)
          : {},
        translations: mergedTranslations,
      }));
      initialLangRef.current = true;
    }
  }, [particularData, languages, selectedLang]);

  // An AI testing run's Overview ("Open in summary"), laid over the saved
  // English once the summary has loaded — `values.bookSummaryId` is only set
  // by the load above, so it is the signal that `values` holds saved data and
  // not the blank defaults. Applied once: a later reload shows what is saved.
  // Asks first when it would replace text, exactly like Generate overview,
  // because Update also syncs About the Book onto the book itself.
  useEffect(() => {
    if (!overviewDraft || draftAppliedRef.current || !particularData || !values.bookSummaryId) return;
    draftAppliedRef.current = true;
    const english = values.translations?.find((t) => t.langCode === "en") || {};
    // Only what the run actually wrote; a section it left out keeps its text.
    const fields = Object.fromEntries(
      Object.entries(overviewDraft.fields || {}).filter(([, html]) => html && !isBlankHtml(html))
    );
    if (Object.keys(fields).length === 0) {
      toast.warn("That AI testing run had no Overview text to fill in — this is the saved summary.");
      return;
    }
    (async () => {
      const replaces = Object.keys(fields).some((field) => !isBlankHtml(english[field] || ""));
      if (
        replaces &&
        !(await confirm({
          title: "Fill in the AI testing run's Overview?",
          body: "The saved Overview fields will be replaced in this form. Nothing is saved until you press Update.",
          confirmLabel: "Fill in",
          cancelLabel: "Keep saved",
          tone: "primary",
        }))
      ) {
        return;
      }
      setValues((previous) => ({
        ...previous,
        translations: previous.translations.map((t) => (t.langCode === "en" ? { ...t, ...fields } : t)),
      }));
      toast.info(
        `Overview filled from the AI testing run${overviewDraft.model ? ` (${overviewDraft.model})` : ""} — review it, then press Update to save.`,
        { autoClose: 8000 }
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewDraft, particularData, values.bookSummaryId]);


  return (
    <React.Fragment>
        <div className={`box_wrapper ${styles.summaryFormContainer}`}>
          <div className={`d-flex justify-content-between fw-bold mb-3 ${styles.summaryFormHeader}`}>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <h4 className="mb-0">Edit Summary</h4>
              {/* What readers made of it, where it can be acted on. */}
              <FeedbackTally summaryId={summaryId} />
            </div>
            <LanguageSelector onSelectLanguage={handleSelectLanguage} />
          </div>
          <div className={`modal_content_lg ${styles.summaryFormBody}`}>
            <div className={`row d-flex justify-content-between my-3 ${styles.summaryFormGrid}`}>
              <div className="col-sm-12 mb-3">
                <label className="label">{t("summary.bookName")}: <span className='text-muted h5'>{translationValues.bookName}</span></label>
                <div className={styles.dropdownContainer}>
                  <input
                    className="px-3 py-3"
                    placeholder={`${t("summary.searchBook")}...`}
                    ref={inputRef}
                    type="text"
                    name="bookName"
                    onChange={(e) => handleSearchTextChange(e.target.value)}
                  />
                 {searchBookName !== "" && booksData.length > 0 ? (
                    <div className={styles.dropdown}>
                      <SearchBookLists
                        onSuccess={(bookId) => handleGetParticularBookDetail(bookId)}
                        SearchBookLists={booksData.filter((item) => includesFolded(item.bookName, searchBookName))}
                      />
                    </div>
                  ) : (
                    searchBookName !== "" && (
                      <div className={styles.dropdown}>
                        <div className={styles.noBooksFound}>
                          <div className={styles.icon}>📚</div>
                          <p className={styles.message}>No books found matching your search "{searchBookName}"</p>
                          <p className={styles.subMessage}>Try searching with a different keyword or add a new book</p>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="col-sm-12 mb-3">
                <label className="label">{t("summary.tagline")}:</label>
                <input
                  className="px-3 py-3 w-100"
                  type="text"
                  name="tagline"
                  placeholder={`${t("summary.enter")} ${t("summary.tagline")}...`}
                  value={translationValues.tagline || ""}
                  onChange={(e) => handleTextEditorChange("tagline", e.target.value, selectedLang)}
                />
              </div>
              <div className="col-sm-12 mb-3">
                <label className="label">{t("summary.relatedBooks")}:</label>
                <Select
                  className={`form-control-sm fw-medium p-0 w-100`}
                  name="relatedBooks"
                  isMulti
                  onChange={(selectedOption) => {
                    handleUpdateTranslationField(
                      "relatedBooks",
                      selectedOption.map((option) => ({ bookName: option.label, bookId: option.value, type: option.type })),
                      selectedLang
                    );
                  }}
                  value={translationValues.relatedBooks.map((option) => ({ label: option.bookName, value: option.bookId }))}
                  options={recommendeBooks.filter((recommend) => recommend.value?.toString() !== particularData.bookSummaryBy?.toString())}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  isDisabled
                />
                {errors && errors.relatedBooks && <p className="error-message d-block">{errors.relatedBooks}</p>}
              </div>
              {relatedBooksCount > 0 && (
                <div className="col-sm-12 mb-3 d-flex justify-content-end">
                  <div className={styles.relatedBooksBadge}>
                    +<span className={styles.relatedBooksCount}>{relatedBooksCount}</span> more {relatedBooksCount > 1 ? 'books' : 'book'} you might like
                  </div>
                </div>
              )}
             <div className="col-sm-12 col-md-4 mb-3">
                <label className="label">{t("summary.authorName")}:</label>
                <div className={`${styles.selectedValueContainer} ${!translationValues.authorName || translationValues.authorName.length === 0 ? styles.empty : ''}`}>
                  {translationValues.authorName && translationValues.authorName.length > 0 ? (
                    translationValues.authorName.map((author, index) => (
                      <span key={index} className={styles.badge}>
                        {author.authorName}
                      </span>
                    ))
                  ) : (
                    <span>No authors selected</span>
                  )}
                </div>
                {errors && errors.authorName && <p className="error-message d-block">{errors.authorName}</p>}
              </div>
              <div className="col-sm-12 col-md-4 mb-3">
                <label className="label">{t("summary.genre")}:</label>
                <div className={`${styles.selectedValueContainer} ${!translationValues.genre || translationValues.genre.length === 0 ? styles.empty : ''}`}>
                  {translationValues.genre && translationValues.genre.length > 0 ? (
                    translationValues.genre.map((genre, index) => (
                      <span key={index} className={styles.badge}>
                        {genre.genreName}
                      </span>
                    ))
                  ) : (
                    <span>No genres selected</span>
                  )}
                </div>
                {errors && errors.genre && <p className="error-message d-block">{errors.genre}</p>}
              </div>
              <div className="col-sm-12 col-md-4 mb-3">
                <PreviewImage
                  label={t("book.coverImage")}
                  // The summary's own cover when it has one, otherwise the
                  // book's — which is what readers already see, because the app
                  // falls back the same way. Showing an empty box here for a
                  // summary that displays a cover in the app reads as a fault.
                  //
                  // Only the preview falls back. `values.coverImage` is left
                  // alone, so Update cannot turn a borrowed cover into the
                  // summary's own copy, frozen at whatever the book had today.
                  image={values.coverImage?.uploadedImageUrl || borrowedCoverUrl}
                  note={
                    !values.coverImage?.uploadedImageUrl && borrowedCoverUrl
                      ? "Using the book's cover — upload one here to give this summary its own."
                      : null
                  }
                  mediaError={editMediaError}
                  error={errors?.coverImage}
                  uploadMedia={(e) => handleUploadMedia(e)}
                />
                {values.coverImage?.uploadedImageUrl && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger mt-2"
                    onClick={handleRemoveCoverImage}
                  >
                    Remove cover
                  </button>
                )}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("summary.uploadAudioNote")}: <span className='text-muted h6'>{t("summary.mention")}</span></label>
                <input
                  className="file"
                  type="file"
                  name="images"
                  multiple
                  accept=".mp3, .wav, .ogg, .m4a"
                  ref={fileInputRef}
                  onChange={(e) => handleUploadAudioAndVideo(e)}
                  onClick={(e) => { e.target.value = null }}
                />
                {editMediaError && <p className="error-message d-block">{editMediaError}</p>}
                {errors && errors.media && errors.media.audio && <p className="error-message d-block">{errors.media.audio}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("summary.uploadVideoNote")}: <span className='text-muted h6'>{t("summary.mention")}</span></label>
                <input
                  className="file"
                  type="file"
                  name="images"
                  multiple
                  accept=".mp4, .webm, .ogg"
                  ref={fileInputRef}
                  onChange={(e) => handleUploadAudioAndVideo(e, selectedLang)}
                  onClick={(e) => { e.target.value = null }}
                />
                {editMediaError && <p className="error-message d-block">{editMediaError}</p>}
                {errors && errors.media && errors.media.video && <p className="error-message d-block">{errors.media.video}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                {audioSrc(translationValues?.media?.audio) && (
                  <>
                    <audio controls className="mt-2 w-100">
                      <source src={audioSrc(translationValues.media.audio)} type="audio/mpeg" />
                    </audio>
                    <RenderProvenance audio={translationValues.media.audio} />
                  </>
                )}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                {translationValues?.media?.video?.uploadedImageUrl && (
                  <video controls width="250" className="mt-2">
                    <source src={convertToCloudFrontUrl(translationValues.media.video.uploadedImageUrl)} type="video/mp4" />
                  </video>
                )}
              </div>
              {selectedLang === "en" && (
                <TranslatePanel
                  summaryId={summaryId}
                  token={adminInfo?.token}
                  translations={values.translations}
                  onTranslated={(language, entry, aboutTheBook) =>
                    // Merge just that language, so unsaved edits elsewhere in
                    // the form survive and a later Update keeps the translation.
                    setValues((prev) => {
                      const merged = {
                        ...entry,
                        aboutTheBook:
                          aboutTheBook ?? prev.translations?.find((t) => t.langCode === language)?.aboutTheBook ?? "",
                        chapters: normalizeChapters(entry.chapters),
                      };
                      const exists = prev.translations?.some((t) => t.langCode === language);
                      return {
                        ...prev,
                        translations: exists
                          ? prev.translations.map((t) => (t.langCode === language ? merged : t))
                          : [...(prev.translations || []), merged],
                      };
                    })
                  }
                  onView={(language) => handleSelectLanguage(language)}
                />
              )}
              <div className="mb-2 d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-baseline gap-2">
                  <strong>Overview</strong>
                  {/* The five fields below, together — the length of the
                      overview is what a writer is working to, not the length
                      of any one box. */}
                  <TextStats
                    parts={[
                      translationValues.aboutTheBook,
                      SHOW_NOTABLE_QUOTES ? translationValues.curatedQuotes : "",
                      translationValues.actionableInsights,
                      translationValues.shouldReadFor,
                      translationValues.reflectionQuestions,
                    ]}
                  />
                </div>
                <GenerateOverviewButton
                  // The edit form's book id lives in `values` (seeded from the
                  // saved summary); the hook's own `bookSummaryId` is only set
                  // when a book is picked on the Add form.
                  bookId={values.bookSummaryId || bookSummaryId}
                  selectedLang={selectedLang}
                  busy={isGeneratingOverview}
                  onGenerate={handleGenerateOverview}
                />
              </div>
              <div className="mb-3">
                <StatLabel
                  label={`${t("summary.aboutTheBook")}:`}
                  parts={[translationValues.aboutTheBook]}
                />
                <SummaryEditor
                  initialValue={translationValues.aboutTheBook}
                  onChange={(content) =>
                      // Normalised because this field alone is synced onto the
                      // BOOK (syncAboutTheBookToBook). Quill emits `<p><br></p>`
                      // for an emptied editor, and the backend treats that as
                      // content — it would translate the markup into all six
                      // languages and write it to the catalogue entry.
                      handleTextEditorChange('aboutTheBook', isBlankHtml(content) ? '' : content, selectedLang)
                    }
                />
                {errors && errors.aboutTheBook && <p className="error-message d-block">{errors.aboutTheBook}</p>}
              </div>
              {/* "Why this book matters" and "Key ideas" are hidden here too. The add
                  form stopped collecting them, so any summary written since then has
                  both empty — and they were still required, which made those summaries
                  impossible to update: the errors sat far above the Update button with
                  nothing at the button to explain the refusal.
              
                  validateEditSummary below drops their errors. Restore the two
                  together with that wrapper and with the matching block in
                  addSummaryPopup. */}
              {/* Notable Quotes: hidden, not removed — see hiddenSections.js. */}
              {SHOW_NOTABLE_QUOTES && (
              <div className="mb-3">
                <StatLabel
                  label={`${t("summary.notableQuotes")}:`}
                  parts={[translationValues.curatedQuotes]}
                />
                <SummaryEditor
                  initialValue={translationValues.curatedQuotes}
                  onChange={(content) => handleTextEditorChange('curatedQuotes', content, selectedLang)}
                />
                {errors && errors.curatedQuotes && <p className="error-message d-block">{errors.curatedQuotes}</p>}
              </div>
              )}
              <div className="mb-3">
                <StatLabel
                  label={`${t("summary.actionableInsights")}:`}
                  parts={[translationValues.actionableInsights]}
                />
                <SummaryEditor
                  initialValue={translationValues.actionableInsights}
                  onChange={(content) => handleTextEditorChange('actionableInsights', content, selectedLang)}
                />
                {errors && errors.actionableInsights && <p className="error-message d-block">{errors.actionableInsights}</p>}
              </div>
              <div className="mb-3">
                <StatLabel
                  label={`${t("summary.shouldReadFor")}:`}
                  parts={[translationValues.shouldReadFor]}
                />
                <SummaryEditor
                  initialValue={translationValues.shouldReadFor}
                  onChange={(content) => handleTextEditorChange('shouldReadFor', content, selectedLang)}
                />
                {errors && errors.shouldReadFor && <p className="error-message d-block">{errors.shouldReadFor}</p>}
              </div>
              <div className="mb-3">
                <StatLabel
                  label={`${t("summary.questionsThisBookForcesYouToAsk")}:`}
                  parts={[translationValues.reflectionQuestions]}
                />
                <SummaryEditor
                  initialValue={translationValues.reflectionQuestions}
                  onChange={(content) => handleTextEditorChange('reflectionQuestions', content, selectedLang)}
                />
                {errors && errors.reflectionQuestions && <p className="error-message d-block">{errors.reflectionQuestions}</p>}
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-baseline gap-2">
                  <label className="label mb-0">{t("summary.breakDown")}:</label>
                  {/* Every chapter's title and body — the summary itself. */}
                  <TextStats
                    parts={(translationValues?.chapters ?? []).flatMap((c) => [
                      c.chapterName,
                      c.content,
                    ])}
                  />
                </div>
                {values.bookSummaryId && particularData.bookSummaryBy && values.bookSummaryId !== particularData.bookSummaryBy && (
                  <p className={styles.chapters} onClick={() => setIsConfirmModalVisible(true)}>Click to update new chapters</p>
                )}
              </div>
              <div className={styles.summaryChapterSection}>
                {translationValues?.chapters?.map((item, index) => (
                  <div className={`mb-3 px-3 py-3 rounded ${styles.summaryChapterCard}`} key={item._id ?? index}>
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-baseline flex-wrap gap-2">
                          <label className="label d-block mb-0">{t("summary.chapter")} {index + 1}:</label>
                          {/* This chapter alone — the breakdown total above is
                              every chapter together. */}
                          <TextStats parts={[item.chapterName, item.content]} />
                        </div>
                        {translationValues?.chapters?.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={async () => {
                              // Narration is matched to a chapter by its _id, so deleting
                              // one strands its rendered audio in S3 and the render has to
                              // be paid for again if the chapter comes back.
                              const warn = item?.audio?.renderId
                                ? ` It has narration, which will be lost.`
                                : "";
                              if (await confirm({ title: `Delete chapter ${index + 1}?`, body: warn.trim() || undefined, confirmLabel: "Delete" })) {
                                handleRemoveAnIndexInChapters(index);
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <input
                        className="form-control px-3 py-3"
                        placeholder={`${t("summary.enter")} ${t("summary.chapter")} ${index + 1}...`}
                        type="text"
                        name="chapterName"
                        value={item.chapterName}
                        onChange={(e) => handleEditChapterValues("chapterName", e.target.value, index, selectedLang)}
                      />
                    </div>
                    <SummaryEditor
                      initialValue={item.content}
                      onChange={(content) => handleEditChapterValues("content", content, index, selectedLang)}
                    />
                    <ChapterNarration
                      chapterId={item._id}
                      audio={item.audio}
                      job={audioJobs[item._id]}
                      onGenerate={handleGenerateNarration}
                      providers={audioProviders}
                      provider={audioProvider}
                      onProviderChange={setAudioProvider}
                      disabled={narrationBusy}
                    />
                    {/* <label className="label mt-3">{t("summary.uploadAudioNote")}: <span className='text-muted'>{t("summary.mention")}</span></label>
                  <div className="row p-0">
                    <div className="d-flex align-items-center gap-2">
                      {audioSrc(item?.audio) && (
                        <div className="col-sm-12 col-md-6">
                            <audio controls className="mt-2 w-100">
                              <source src={audioSrc(item.audio)} type="audio/mpeg" />
                            </audio>
                            <RenderProvenance audio={item.audio} />
                        </div>
                      )}
                      <div className={`col-sm-12 ${audioSrc(item?.audio) ? 'col-md-6' : 'col-md-12'}`}>
                        <div className="d-flex align-items-center gap-2 justify-content-between">
                          <input
                            className="file"
                            type="file"
                            name="images"
                            multiple
                            ref={fileInputRef}
                            onChange={(e) => handleUploadAudioFileInChapter(e, index, selectedLang)}
                            onClick={(e) => { e.target.value = null }}
                          />
                          <svg className="delete-icon" role="button" width="22" height="22" viewBox="0 0 14 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg" onClick={() => handleRemoveAnIndexInChapters(index)}>
                            <path d="M1 16C1 17.1 1.9 18 3 18H11C12.1 18 13 17.1 13 16V6C13 4.9 12.1 4 11 4H3C1.9 4 1 4.9 1 6V16ZM13 1H10.5L9.79 0.29C9.61 0.11 9.35 0 9.09 0H4.91C4.65 0 4.39 0.11 4.21 0.29L3.5 1H1C0.45 1 0 1.45 0 2C0 2.55 0.45 3 1 3H13C13.55 3 14 2.55 14 2C14 1.45 13.55 1 13 1Z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  {editMediaError && <p className="error-message d-block">{editMediaError}</p>} */}
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-end align-items-center gap-1">
                <button type="button" className="btn btn-outline-dark" onClick={handleCreateAnIndexInChapters}>Add More</button>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 px-2 mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={isSubmit}>Cancel</button>
              {/* Update saves and stops. This one also carries the chapters
                  into every language — building the ones that do not exist
                  yet, and refreshing only the chapters that changed in the
                  ones that do. The Overview is saved in English either way. */}
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={isSubmit}
                onClick={(event) => {
                  translateRef.current = true;
                  handleSubmit(event);
                }}
                title="Saves, then translates the chapters into Hindi, German, French, Spanish and Japanese. The Overview is not translated."
              >
                Update &amp; translate
              </button>
              <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Please wait ... It might take a few minutes to complete..." text="Update" />
          </div>
        </div>
      <Modal
        isOpen={isConfirmModalVisible}
        onRequestClose={() => !isSubmitSummary && setIsConfirmModalVisible(false)}
        className="modal_body_sm"
        overlayClassName="modal_overlay"
      >
        <div className={`box_wrapper ${styles.confirmModalWrapper}`}>
          <div className={`d-flex justify-content-between fw-bold mb-3 ${styles.modalHeader}`}>
            <h4 className={styles.modalTitle}>Auto Generate Chapters</h4>
          </div>
          <div className={`modal_content ${styles.modalContent}`}>
            <div className={`mb-4 ${styles.messageContainer}`}>
              {isSubmitSummary ? (
                <p className={`mb-3 ${styles.messageText}`}>
                  Please wait ... It might take a few minutes to complete...
                </p>
              ) : (
                <p className={`mb-3 ${styles.messageText}`}>
                  Are you sure you want to auto generate book chapters using AI? This will create chapter summaries based on the book content.
                </p>
              )}
            </div>
            <div className={`d-flex justify-content-end gap-2 ${styles.buttonContainer}`}>
              <button
                type="button"
                className={`btn btn-outline-secondary ${styles.cancelBtn}`}
                onClick={() => setIsConfirmModalVisible(false)}
                disabled={isSubmitSummary}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-dark d-flex align-items-center justify-content-center gap-2 ${styles.generateBtn}`}
                onClick={async () => {
                  generatingRef.current = true;
                  try {
                    await handleAutoGenerateChapters(values.bookSummaryId || bookSummaryId);
                  } catch (error) {
                    generatingRef.current = false;
                    console.error("Error generating chapters:", error);
                  }
                }}
                disabled={isSubmitSummary}
              >
                {isSubmitSummary && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {isSubmitSummary ? "Please wait ... It might take a few minutes to complete..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default memo(EditBookSummaryPopup)


